import {
  FormBulletIcon,
  FormError,
  FormErrorIcon,
  FormInputBase,
  FormSuccessIcon,
} from '@dx-ui/osc-form';
import { useTranslation } from 'next-i18next';
import { useState, useId } from 'react';
import cx from 'classnames';
import { get, useFormContext, useFormState } from 'react-hook-form';
import { sendRewardAsync } from '@dx-ui/framework-conductrics';
import { logError } from '@dx-ui/framework-logger';
import { passwordRequirementsValidation } from './login-form.utilities';
import { Button } from '@dx-ui/global-ui-prism';

interface ShowHidePasswordInput extends FormInputBase<React.InputHTMLAttributes<HTMLInputElement>> {
  hideFormError?: boolean;
  isJoinForm?: boolean;
  hasPasswordCriteria?: boolean;
  passwordCriteriaClassName?: string;
  containerClassName?: string;
}

export const ShowHidePasswordInput: React.FC<React.PropsWithChildren<ShowHidePasswordInput>> = ({
  name,
  loading,
  registerOptions,
  labelClassName,
  hideFormError,
  label,
  isJoinForm,
  hasPasswordCriteria,
  passwordCriteriaClassName,
  containerClassName,
  ...rest
}) => {
  const [passwordFieldType, setPasswordFieldType] = useState<'password' | 'text'>('password');
  const { t } = useTranslation('osc-login');
  const { register } = useFormContext();
  const { errors, touchedFields } = useFormState({ name });
  const fieldError = get(errors, name);
  const touchedField = get(touchedFields, name);
  const hasError = !!fieldError;
  const id = useId();
  const errorId = `input-error-${id}`;
  const helperId = `input-helper-${id}`;

  const passRequirements = [
    { type: 'mustBe', message: t('password.requirements.mustBe') },
    { type: 'containUpper', message: t('password.requirements.containUpper') },
    { type: 'containLower', message: t('password.requirements.containLower') },
    { type: 'containOneNumber', message: t('password.requirements.containOneNumber') },
  ];

  const sendConductricsReward = (goalId: string) => {
    sendRewardAsync(goalId).catch((error: string) =>
      logError('sendRewardError', error, `RewardID ${goalId}`)
    );
  };

  const togglePasswordVisibility = () => {
    if (hasError && isJoinForm) {
      sendConductricsReward('g-show-hide-engagement-with-error');
    }
    if (passwordFieldType === 'password') {
      sendConductricsReward(isJoinForm ? 'g-show-password-join' : 'g-show-password');
      setPasswordFieldType('text');
    } else {
      sendConductricsReward(isJoinForm ? 'g-hide-password-join' : 'g-hide-password');
      setPasswordFieldType('password');
    }
  };

  const sharedRegisterOptions = {
    validate: hasPasswordCriteria
      ? {
          required: (value: string) =>
            Object.values(passwordRequirementsValidation).every((regex: RegExp) =>
              regex.test(value)
            ) || t('password.criteriaError'),
          mustBe: (value: string) =>
            passwordRequirementsValidation.length.test(value) || t('password.requirements.mustBe'),
          containUpper: (value: string) =>
            passwordRequirementsValidation.uppercase.test(value) ||
            t('password.requirements.containUpper'),
          containLower: (value: string) =>
            passwordRequirementsValidation.lowercase.test(value) ||
            t('password.requirements.containLower'),
          containOneNumber: (value: string) =>
            passwordRequirementsValidation.numOrSpecial.test(value) ||
            t('password.requirements.containOneNumber'),
        }
      : undefined,
    required: hasPasswordCriteria ? false : t('password.requiredError'),
    ...registerOptions,
  };

  return (
    <div className={cx({ [containerClassName as string]: !!containerClassName })}>
      <label
        className={cx(labelClassName, 'label', {
          'text-surface-error': hasError,
        })}
        data-e2e="pwdLabel"
        htmlFor={name}
      >
        {label || t('password.label')}
      </label>
      <div className="relative mt-1">
        <input
          className={cx('form-input w-full', {
            'form-error': hasError,
          })}
          disabled={loading}
          aria-label={rest?.['aria-label'] || t('password.label')}
          id={name}
          aria-invalid={hasError}
          {...rest}
          {...register(name, sharedRegisterOptions)}
          aria-describedby={`${!hideFormError && !hasPasswordCriteria ? errorId : ''} ${hasPasswordCriteria ? helperId : ''} ${rest?.['aria-describedby'] || ''}`}
          type={passwordFieldType}
        />
        <Button
          fill="flat"
          accessibleLabel={
            passwordFieldType === 'password'
              ? t('showHide.showPasswordAriaLabel')
              : t('showHide.hidePasswordAriaLabel')
          }
          label={passwordFieldType === 'password' ? t('showHide.show') : t('showHide.hide')}
          onClick={togglePasswordVisibility}
          aria-controls="password"
          className="absolute end-2 top-1/2 min-w-20 -translate-y-1/2"
          iconProps={[
            {
              name: 'unhide',
              size: 'md',
              className: passwordFieldType === 'password' ? undefined : 'sr-only',
            },
            {
              name: 'hide',
              size: 'md',
              className: passwordFieldType !== 'password' ? undefined : 'sr-only',
            },
          ]}
        />
        <output className="sr-only">
          {passwordFieldType === 'password'
            ? t('showHide.passwordHiddenAria')
            : t('showHide.passwordVisibleAria')}
        </output>
      </div>
      {!hideFormError && !hasPasswordCriteria ? (
        <FormError error={fieldError} id={errorId} className="pt-1 font-normal" />
      ) : null}
      {hasPasswordCriteria ? (
        <ul
          id={helperId}
          className={cx('text--sm mt-2 grid gap-2 md:grid-cols-2', {
            [passwordCriteriaClassName as string]: !!passwordCriteriaClassName,
          })}
        >
          {passRequirements.map(({ message, type }) => {
            const hasRequirementError = fieldError?.types?.[type];
            return (
              <li
                key={`passRequirement-${type}`}
                className={cx('flex items-center gap-1', {
                  'text-surface-error': hasRequirementError,
                  'text-surface-success': !hasRequirementError && touchedField,
                })}
              >
                {hasRequirementError && <FormErrorIcon />}
                {!hasRequirementError && !touchedField && <FormBulletIcon />}
                {!hasRequirementError && touchedField && <FormSuccessIcon />}
                <span>{message}</span>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

export default ShowHidePasswordInput;
